<template>
  <ToastVue />
  <HeaderPanel />
  <div class="kinescope-page">
    <h1>Kinescope конструктор встраивания</h1>
    <p class="subtitle">Заполните параметры и нажмите «Получить» — готовый скрипт скопируется в буфер обмена.</p>

    <div class="form-section">
      <h2>Видео</h2>
      <div class="field">
        <label for="video-input">URL или ID видео</label>
        <input
          id="video-input"
          v-model="videoInput"
          type="text"
          placeholder="https://kinescope.io/123456789 или 123456789"
          @blur="onVideoInputBlur"
        />
      </div>
      <div class="field row">
        <div class="half">
          <label for="player-id">ID контейнера плеера</label>
          <input id="player-id" v-model="playerContainerId" type="text" />
        </div>
        <div class="half">
          <label for="chapters-id">ID контейнера таймкодов</label>
          <input id="chapters-id" v-model="chaptersContainerId" type="text" />
        </div>
      </div>
    </div>

    <div class="form-section collapsible-section">
      <button type="button" class="collapsible-trigger" :aria-expanded="playerSettingsOpen" @click="playerSettingsOpen = !playerSettingsOpen">
        <span class="collapsible-title">Настройки плеера</span>
        <span class="collapsible-icon" aria-hidden="true" :class="{ open: playerSettingsOpen }">▶</span>
      </button>
      <Transition name="collapsible">
        <div v-if="playerSettingsOpen" class="collapsible-content">
          <div class="options-grid">
          <div class="field">
            <label for="autoplay">Автовоспроизведение</label>
            <select id="autoplay" v-model="autoPlay">
              <option value="false">Выключено</option>
              <option value="true">Сразу</option>
              <option value="viewable">При появлении в зоне видимости</option>
            </select>
          </div>
          <div class="field">
            <label for="preload">Предзагрузка</label>
            <select id="preload" v-model="preload">
              <option value="metadata">metadata</option>
              <option value="none">none</option>
              <option value="auto">auto</option>
            </select>
          </div>
          <div class="field">
            <label for="language">Язык UI</label>
            <select id="language" v-model="language">
              <option value="ru">Русский</option>
              <option value="en">English</option>
            </select>
          </div>
          <div class="field checkbox">
            <label>
              <input v-model="muted" type="checkbox" />
              Без звука (muted)
            </label>
          </div>
          <div class="field checkbox">
            <label>
              <input v-model="controls" type="checkbox" />
              Показывать элементы управления
            </label>
          </div>
        </div>
        </div>
      </Transition>
    </div>

    <div class="form-section collapsible-section">
      <button type="button" class="collapsible-trigger" :aria-expanded="analyticsSettingsOpen" @click="analyticsSettingsOpen = !analyticsSettingsOpen">
        <span class="collapsible-title">Аналитика эфира</span>
        <span class="collapsible-icon" aria-hidden="true" :class="{ open: analyticsSettingsOpen }">▶</span>
      </button>
      <Transition name="collapsible">
        <div v-if="analyticsSettingsOpen" class="collapsible-content">
          <p class="hint">Событие старта эфира + накопленное время просмотра.</p>
          <label class="field checkbox">
            <input v-model="analyticsEnabled" type="checkbox" />
            Включить аналитику в сниппет
          </label>
          <div class="field row">
            <div class="half">
              <label for="analytics-interval">Период отправки времени (сек)</label>
              <input id="analytics-interval" v-model.number="analyticsIntervalSec" type="number" min="5" max="120" />
            </div>
            <div class="half">
              <label for="analytics-endpoint">Webhook URL (опц.)</label>
              <input id="analytics-endpoint" v-model="analyticsEndpoint" type="text" placeholder="https://.../video-events" />
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <div class="form-section">
      <h2>Таймкоды (главы)</h2>
      <p class="hint">Укажите минуты, секунды и название. Выберите, где показывать таймкоды.</p>
      <div class="chapters-placement">
        <label class="field checkbox">
          <input v-model="chaptersInVideo" type="checkbox" />
          В видео (главы в плеере)
        </label>
        <label class="field checkbox">
          <input v-model="chaptersUnderVideo" type="checkbox" />
          Под видео (список «MM:SS — Название»)
        </label>
      </div>
      <div class="chapters-list">
        <div
          v-for="(ch, index) in chapters"
          :key="ch.id"
          class="chapter-row"
        >
          <input
            v-model.number="ch.minutes"
            type="number"
            min="0"
            placeholder="Мин"
            class="input-min"
          />
          <input
            v-model.number="ch.seconds"
            type="number"
            min="0"
            max="59"
            placeholder="Сек"
            class="input-sec"
          />
          <input
            v-model="ch.title"
            type="text"
            placeholder="Название главы"
            class="input-title"
          />
          <button
            type="button"
            class="btn-remove"
            aria-label="Удалить"
            @click="removeChapter(index)"
          >
            ×
          </button>
        </div>
      </div>
      <button type="button" class="btn-add" @click="addChapter">+ Добавить таймкод</button>
    </div>

    <div class="form-section actions">
      <button type="button" class="btn-primary" @click="generateAndCopy">
        Получить
      </button>
    </div>

    <div class="form-section">
      <h2>Сгенерированный код</h2>
      <p class="hint">Вставьте этот код на страницу в блок HTML.</p>
      <textarea
        ref="snippetRef"
        v-model="generatedSnippet"
        readonly
        class="snippet-textarea"
        rows="20"
        spellcheck="false"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import HeaderPanel from '@/components/HeaderPanel.vue'

function escapeJsString(s) {
  if (s == null) return '""'
  return JSON.stringify(String(s))
}

export default {
  name: 'KinescopePage',
  components: { HeaderPanel },
  data() {
    return {
      videoInput: '',
      playerContainerId: '',
      chaptersContainerId: '',
      autoPlay: 'false',
      preload: 'metadata',
      muted: false,
      controls: true,
      language: 'ru',
      chaptersInVideo: true,
      chaptersUnderVideo: true,
      chapters: [
        { id: 1, minutes: 0, seconds: 0, title: '' }
      ],
      nextChapterId: 2,
      analyticsEnabled: false,
      analyticsIntervalSec: 15,
      analyticsEndpoint: '',
      generatedSnippet: '',
      playerSettingsOpen: false,
      analyticsSettingsOpen: false
    }
  },
  computed: {
    videoId() {
      const v = (this.videoInput || '').trim()
      if (!v) return ''
      if (/^https?:\/\//i.test(v)) {
        try {
          const path = new URL(v).pathname
          const segment = path.split('/').filter(Boolean).pop()
          return segment || ''
        } catch (_) {
          return ''
        }
      }
      return v.replace(/^.*\/+/, '')
    },
    resolvedVideoUrl() {
      const v = (this.videoInput || '').trim()
      if (!v) return ''
      if (/^https?:\/\//i.test(v)) return v
      const id = this.videoId
      return id ? `https://kinescope.io/${id}` : ''
    },
    normalizedChapters() {
      return this.chapters
        .filter(ch => ch.title != null && String(ch.title).trim() !== '')
        .map(ch => {
          const m = Math.max(0, parseInt(ch.minutes, 10) || 0)
          const s = Math.max(0, Math.min(59, parseInt(ch.seconds, 10) || 0))
          return { position: m * 60 + s, title: String(ch.title).trim() }
        })
        .sort((a, b) => a.position - b.position)
    }
  },
  watch: {
    videoId(id) {
      this.syncContainerIdsFromVideo(id)
    }
  },
  methods: {
    syncContainerIdsFromVideo(id = this.videoId) {
      const videoId = (id || '').trim()
      if (!videoId) return
      this.playerContainerId = videoId
      this.chaptersContainerId = `${videoId}-chapters`
    },
    addChapter() {
      this.chapters.push({
        id: this.nextChapterId++,
        minutes: 0,
        seconds: 0,
        title: ''
      })
    },
    removeChapter(index) {
      this.chapters.splice(index, 1)
    },
    formatTime(seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return m + ':' + String(s).padStart(2, '0')
    },
    generateSnippet() {
      const videoId = this.videoId
      const playerId = this.playerContainerId.trim() || videoId || 'kinescope-player'
      const chaptersId = this.chaptersContainerId.trim() || (videoId ? `${videoId}-chapters` : 'kinescope-chapters')
      const videoUrl = this.resolvedVideoUrl
      if (!videoUrl) {
        return 'Укажите URL или ID видео.'
      }
      const chaptersData = this.normalizedChapters
      const chaptersJson = JSON.stringify(chaptersData, null, 2)
        .replace(/\n/g, '\n    ')
      const autoPlayVal = this.autoPlay === 'true' ? true : this.autoPlay === 'viewable' ? '"viewable"' : false
      const behaviorParts = [
        `preload: ${escapeJsString(this.preload)}`,
        `autoPlay: ${autoPlayVal}`,
        `muted: ${this.muted}`
      ]
      const behaviorStr = '{\n      ' + behaviorParts.join(',\n      ') + '\n    }'
      const uiStr = `{\n      language: ${escapeJsString(this.language)},\n      controls: ${this.controls}\n    }`
      const playlistItemStr = this.chaptersInVideo && chaptersData.length > 0
        ? `{ chapters: chapters }`
        : '{}'
      const analyticsIntervalSec = Math.max(5, Math.min(120, parseInt(this.analyticsIntervalSec, 10) || 15))
      const analyticsEndpoint = (this.analyticsEndpoint || '').trim() || 'https://ncht.bz/api/kinescope/ingest'
      const chaptersHelperBlock =
        this.chaptersUnderVideo && chaptersData.length > 0
          ? `
  function renderKinescopeChaptersList(player) {
    var container = document.getElementById(chaptersContainerId);
    if (!container) return;
    container.innerHTML = '';
    chapters.forEach(function(ch) {
      var line = document.createElement('div');
      var a = document.createElement('a');
      a.href = '#';
      a.textContent = formatTime(ch.position) + ' — ';
      a.onclick = function(e) {
        e.preventDefault();
        player.seekTo(ch.position);
        player.play();
      };
      line.appendChild(a);
      line.appendChild(document.createTextNode(ch.title));
      container.appendChild(line);
    });
  }
`
          : ''
      const chaptersRenderCall =
        this.chaptersUnderVideo && chaptersData.length > 0
          ? `
      renderKinescopeChaptersList(player);
`
          : ''
      const analyticsBlock = this.analyticsEnabled
        ? `
    var analyticsState = {
      hasPlayed: false,
      hasViewed80: false,
      maxPositionSeconds: 0,
      watchSeconds: 0,
      lastKnownTime: 0,
      lastSentWatchSeconds: 0,
      timer: null
    };

    function getUserId() {
      return window.accountUserId != null ? String(window.accountUserId) : null;
    }

    function pushAnalytics(eventName, payload) {
      var eventPayload = Object.assign({
        event: eventName,
        source: (window.location && window.location.href) ? String(window.location.href) : 'about:blank',
        user_id: getUserId(),
        video_id: ${escapeJsString(this.videoId)},
        video_url: videoUrl,
        ts: Date.now()
      }, payload || {});

      ${analyticsEndpoint
        ? `try {
        fetch(${escapeJsString(analyticsEndpoint)}, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify(eventPayload)
        });
      } catch (_) {}`
        : ''}
    }

    function asNumber(v) {
      var n = Number(v);
      return Number.isFinite(n) ? n : 0;
    }

    function resolveCurrentTime(playerInstance) {
      try {
        if (typeof playerInstance.getCurrentTime === 'function') {
          var maybe = playerInstance.getCurrentTime();
          if (maybe && typeof maybe.then === 'function') {
            return maybe.then(asNumber).catch(function() { return 0; });
          }
          return Promise.resolve(asNumber(maybe));
        }
      } catch (_) {}
      return Promise.resolve(0);
    }

    function tickWatchTime(playerInstance) {
      resolveCurrentTime(playerInstance).then(function(currentTime) {
        var prev = analyticsState.lastKnownTime;
        analyticsState.lastKnownTime = currentTime;
        if (currentTime > analyticsState.maxPositionSeconds) {
          analyticsState.maxPositionSeconds = currentTime;
        }
        var delta = currentTime - prev;
        if (delta > 0 && delta < 10) {
          analyticsState.watchSeconds += delta;
        }
        if (analyticsState.watchSeconds - analyticsState.lastSentWatchSeconds >= ${analyticsIntervalSec}) {
          analyticsState.lastSentWatchSeconds = analyticsState.watchSeconds;
          pushAnalytics('kinescope_watch_progress', {
            watch_seconds: Math.floor(analyticsState.watchSeconds),
            position_seconds: Math.floor(currentTime)
          });
        }

        if (!analyticsState.hasViewed80 && analyticsState.maxPositionSeconds > 0) {
          var watchProgress = analyticsState.watchSeconds / analyticsState.maxPositionSeconds;
          if (watchProgress >= 0.8) {
            analyticsState.hasViewed80 = true;
            pushAnalytics('kinescope_view_80', {
              watch_seconds: Math.floor(analyticsState.watchSeconds),
              position_seconds: Math.floor(currentTime)
            });
          }
        }
      });
    }

    function flushWatchTime(playerInstance, eventName) {
      resolveCurrentTime(playerInstance).then(function(currentTime) {
        if (analyticsState.lastKnownTime > 0) {
          var delta = currentTime - analyticsState.lastKnownTime;
          if (delta > 0 && delta < 10) {
            analyticsState.watchSeconds += delta;
          }
          analyticsState.lastKnownTime = currentTime;
        }
        pushAnalytics(eventName || 'kinescope_watch_final', {
          watch_seconds: Math.floor(analyticsState.watchSeconds),
          position_seconds: Math.floor(currentTime)
        });
      });
    }

    function bindPlayerEvent(playerInstance, eventName, handler) {
      try {
        if (playerInstance && typeof playerInstance.on === 'function') {
          playerInstance.on(eventName, handler);
          return true;
        }
      } catch (_) {}
      try {
        if (playerInstance && typeof playerInstance.addEventListener === 'function') {
          playerInstance.addEventListener(eventName, handler);
          return true;
        }
      } catch (_) {}
      return false;
    }

    function setupAnalytics(playerInstance) {
      bindPlayerEvent(playerInstance, 'play', function() {
        if (!analyticsState.hasPlayed) {
          analyticsState.hasPlayed = true;
          pushAnalytics('kinescope_play_started', { reached_stream: 1 });
        }
        if (!analyticsState.timer) {
          analyticsState.timer = setInterval(function() {
            tickWatchTime(playerInstance);
          }, 1000);
        }
      });

      bindPlayerEvent(playerInstance, 'pause', function() {
        flushWatchTime(playerInstance, 'kinescope_watch_pause');
      });

      bindPlayerEvent(playerInstance, 'ended', function() {
        flushWatchTime(playerInstance, 'kinescope_watch_ended');
      });

      window.addEventListener('beforeunload', function() {
        flushWatchTime(playerInstance, 'kinescope_watch_unload');
      });
    }
`
        : ''

      return `<div class="kinescope-player-wrap" style="width:100%;position:relative;aspect-ratio:16/9"><div id="${playerId.replace(/"/g, '&quot;')}"></div></div>
<div style="margin-top: 16px;" id="${chaptersId.replace(/"/g, '&quot;')}"></div>
<script>
(function() {
  var playerContainerId = ${escapeJsString(playerId)};
  var chaptersContainerId = ${escapeJsString(chaptersId)};
  var videoUrl = ${escapeJsString(videoUrl)};
  var chapters = ${chaptersJson};

  var style = document.getElementById('kinescope-embed-styles');
  if (!style) {
    style = document.createElement('style');
    style.id = 'kinescope-embed-styles';
    style.textContent = '.kinescope-player-wrap{width:100%;position:relative;aspect-ratio:16/9}.kinescope-player-wrap>*{position:absolute;top:0;left:0;width:100%;height:100%;}';
    document.head.appendChild(style);
  }

  function formatTime(seconds) {
    var m = Math.floor(seconds / 60);
    var s = seconds % 60;
    return m + ':' + String(s).padStart(2, '0');
  }

  function loadKinescopeApi(callback) {
    if (window.__kinescopePlayerFactory) {
      callback(window.__kinescopePlayerFactory);
      return;
    }
    window.__kinescopeApiWaiters = window.__kinescopeApiWaiters || [];
    window.__kinescopeApiWaiters.push(callback);
    if (window.__kinescopeApiBootstrapped) return;
    window.__kinescopeApiBootstrapped = true;

    var tag = document.querySelector('script[src*="player.kinescope.io"][src*="iframe.player.js"]');
    if (!tag) {
      tag = document.createElement('script');
      tag.src = 'https://player.kinescope.io/latest/iframe.player.js';
      var firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    window.onKinescopeIframeAPIReady = function(playerFactory) {
      window.__kinescopePlayerFactory = playerFactory;
      var waiters = window.__kinescopeApiWaiters || [];
      window.__kinescopeApiWaiters = [];
      waiters.forEach(function(cb) {
        try { cb(playerFactory); } catch (_) {}
      });
    };
  }

  loadKinescopeApi(function(playerFactory) {${chaptersHelperBlock}${analyticsBlock}
    playerFactory
      .create(playerContainerId, {
        url: videoUrl,
        size: { width: '100%', height: '100%' },
        behavior: ${behaviorStr},
        ui: ${uiStr},
        playlist: [${playlistItemStr}]
      })
      .then(function(player) {${chaptersRenderCall}
${analyticsBlock ? '        setupAnalytics(player);' : ''}
      })
      .catch(function(error) {
        try {
          var isNetworkFetchError =
            error &&
            (error.type === 'error') &&
            error.data &&
            /Failed to fetch/i.test(String(error.data.message || error.data));
          if (!isNetworkFetchError) {
            console.warn('Kinescope player creation failed, retrying with minimal config', error);
          }
        } catch (_) {
          // ignore classification errors
        }
        return playerFactory
          .create(playerContainerId, {
            url: videoUrl,
            size: { width: '100%', height: '100%' }
          })
          .then(function(player) {${chaptersRenderCall}
${analyticsBlock ? '            setupAnalytics(player);' : ''}
          });
      });
  });
})();
</scr` + `ipt>`
    },
    async generateAndCopy() {
      const videoId = this.videoId
      const chapters = this.normalizedChapters
      if (videoId && chapters.length > 0) {
        try {
          await this.syncChaptersToKinescope(videoId, chapters)
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || 'Ошибка синхронизации глав с Kinescope.'
          this.$toast.add({ severity: 'error', summary: 'Синхронизация', detail: msg, life: 5000 })
          return
        }
      }
      const snippet = this.generateSnippet()
      this.generatedSnippet = snippet
      try {
        await navigator.clipboard.writeText(snippet)
        this.$toast.add({ severity: 'success', summary: 'Готово', detail: 'Скопировано в буфер обмена.', life: 3000 })
      } catch (_) {
        this.$refs.snippetRef?.select?.()
        const ok = document.execCommand('copy')
        if (ok) {
          this.$toast.add({ severity: 'success', summary: 'Готово', detail: 'Скопировано.', life: 3000 })
        } else {
          this.$toast.add({ severity: 'warn', summary: 'Копирование', detail: 'Скопируйте вручную из поля ниже.', life: 4000 })
        }
      }
    },
    async fetchVideoChapters(videoId) {
      const url = `https://ncht.bz/api/kinescope-videos/${encodeURIComponent(videoId)}`
      const { data } = await axios.get(url)
      const raw = data?.data ?? data
      const list = Array.isArray(raw?.chapters) ? raw.chapters : []
      if (list.length === 0) return
      this.chapters = list.map((ch, i) => ({
        id: this.nextChapterId + i,
        minutes: Math.floor((ch.position ?? 0) / 60),
        seconds: Math.round((ch.position ?? 0) % 60),
        title: ch.title ?? ''
      }))
      const maxId = Math.max(0, ...this.chapters.map(c => c.id))
      this.nextChapterId = maxId + 1
    },
    async syncChaptersToKinescope(videoId, chapters) {
      const url = `https://ncht.bz/api/kinescope-videos/${encodeURIComponent(videoId)}`
      const body = { chapters: chapters.map(ch => ({ position: ch.position, title: ch.title })) }
      await axios.patch(url, body)
    },
    async onVideoInputBlur() {
      const id = this.videoId
      if (!id) return
      this.$toast.add({ severity: 'info', summary: 'Загрузка', detail: 'Загрузка глав…', life: 2000 })
      try {
        await this.fetchVideoChapters(id)
        if (this.chapters.length) {
          this.$toast.add({ severity: 'success', summary: 'Главы', detail: 'Главы загружены из Kinescope.', life: 3000 })
        }
      } catch (err) {
        const msg = err?.response?.status === 404 ? 'Видео не найдено.' : (err?.response?.data?.message || err?.message || 'Ошибка загрузки.')
        this.$toast.add({ severity: 'error', summary: 'Загрузка глав', detail: msg, life: 5000 })
      }
    }
  }
}
</script>

<style scoped>
.kinescope-page {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  color: var(--p-text-color, #1f2937);
}
.kinescope-page h1 {
  margin: 0 0 0.25rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--p-text-color, #111827);
}
.subtitle {
  margin: 0;
  color: var(--p-text-muted-color, #4b5563);
  font-size: 0.9rem;
}
.form-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.form-section h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--p-text-color, #111827);
}
.hint {
  margin: 0;
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #4b5563);
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.field label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--p-text-color, #374151);
}
.field input[type="text"],
.field input[type="number"],
.field select {
  padding: 0.5rem;
  border: 1px solid var(--p-input-border-color, #d1d5db);
  border-radius: 6px;
  font-size: 1rem;
  background: var(--p-input-background, var(--p-surface-0, #fafafa));
  color: var(--p-input-text-color, var(--p-text-color, #1f2937));
}
.field input::placeholder {
  color: var(--p-text-muted-color, #6b7280);
}
.field select option {
  background: var(--p-surface-0, #fff);
  color: var(--p-text-color, #1f2937);
}
.field.row {
  flex-direction: row;
  flex-wrap: wrap;
}
.field.row .half {
  flex: 1;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.collapsible-section {
  gap: 0;
}
.collapsible-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 0;
  margin: 0;
  border: none;
  border-radius: 4px;
  background: none;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  color: var(--p-text-color, #111827);
  text-align: left;
}
.collapsible-trigger:hover {
  background: var(--p-surface-hover, rgba(0, 0, 0, 0.05));
}
.collapsible-icon {
  opacity: 0.7;
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}
.collapsible-icon.open {
  transform: rotate(90deg);
}
.collapsible-content {
  overflow: hidden;
  padding-top: 0.5rem;
  padding-bottom: 0.25rem;
}
.collapsible-enter-active.collapsible-content,
.collapsible-leave-active.collapsible-content {
  transition: max-height 0.3s ease, opacity 0.25s ease;
}
.collapsible-enter-from.collapsible-content,
.collapsible-leave-to.collapsible-content {
  max-height: 0;
  opacity: 0;
}
.collapsible-enter-to.collapsible-content,
.collapsible-leave-from.collapsible-content {
  max-height: 420px;
  opacity: 1;
}
.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
}
.field.checkbox label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: normal;
}
.chapters-placement {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.chapters-placement .checkbox {
  flex-direction: row;
}
.chapters-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.chapter-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.input-min { width: 4rem; }
.input-sec { width: 4rem; }
.input-title { flex: 1; min-width: 160px; }
.chapter-row input {
  padding: 0.4rem;
  border: 1px solid var(--p-input-border-color, #d1d5db);
  border-radius: 6px;
  background: var(--p-input-background, var(--p-surface-0, #fafafa));
  color: var(--p-input-text-color, var(--p-text-color, #1f2937));
}
.chapter-row input::placeholder {
  color: var(--p-text-muted-color, #6b7280);
}
.btn-remove {
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  background: var(--p-button-danger-background, #e57373);
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1;
}
.btn-remove:hover {
  opacity: 0.9;
}
.btn-add {
  align-self: flex-start;
  padding: 0.5rem 1rem;
  border: 1px dashed var(--p-input-border-color, #9ca3af);
  background: var(--p-surface-100, #f9fafb);
  color: var(--p-text-color, #374151);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
}
.btn-add:hover {
  background: var(--p-surface-hover, #f3f4f6);
  border-color: var(--p-input-border-color, #6b7280);
}
.actions {
  flex-direction: row;
  align-items: center;
  gap: 1rem;
}
.btn-primary {
  padding: 0.6rem 1.5rem;
  border: none;
  border-radius: 6px;
  background: var(--p-primary-color, #2563eb);
  color: var(--p-primary-contrast-color, var(--p-primary-inverse-color, #fff));
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.btn-primary:hover {
  filter: brightness(0.95);
}
.snippet-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--p-input-border-color, #d1d5db);
  border-radius: 6px;
  font-family: ui-monospace, monospace;
  font-size: 0.85rem;
  resize: vertical;
  background: var(--p-input-background, var(--p-surface-0, #fafafa));
  color: var(--p-input-text-color, var(--p-text-color, #1f2937));
}

/* Тёмная тема по классу на html/body (PrimeVue и др.) */
</style>
